import { createSelector, createSlice } from "@reduxjs/toolkit";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { dateRangeOverlap } from "../../common/utils/dateUtils";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = meeting => meeting.id;

export const meetingStaffSlice = createSlice({
    name: 'staffMeetings',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.ids.forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(meeting => state.data[meeting.id] = meeting);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(meeting => state.data[meeting.id] = meeting);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(meeting => delete state.data[meeting.id]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => meetingStaffSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'meeting_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'meeting_one');

const fetchMeeting = aggregatingFetch(
    id => network => network.post('/api/v2/staffMeetings', [id],  data => meetingStaffSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/staffMeetings', ids, data => meetingStaffSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchMeetingsInRange = (start, end) => network => network.get(`/api/v2/staffMeetings/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, meetingStaffSlice.actions.addOrUpdate, visualizeFailure('get'));

// #endregion

// #region selectors
const selectMeetingData = state => state.staffMeetings.data;
const selectMeeting = id => createSelector([selectMeetingData], meetings => meetings[id]);
const selectMeetings = ids => createSelector([selectMeetingData], meetings => ids.reduce((result, id) => { if (meetings[id]) { result.push(meetings[id]); } return result; }, []));

const selectMeetingsInRange = (start, end) => createSelector([selectMeetingData], meetings => {
    return Object.values(meetings).filter(m => dateRangeOverlap(m, { start, end }));
})
// #endregion

const listen = {
    meetingCreated: (meeting, { dispatch, websocket }) => {
        markFetched(websocket, 'staffMeeting', key(meeting))
        dispatch(meetingStaffSlice.actions.addOrUpdate(meeting))
    },
    meetingUpdated: (meeting, { dispatch }) => dispatch(meetingStaffSlice.actions.addOrUpdate(meeting)),
    meetingRemoved: (meeting, { dispatch }) => dispatch(meetingStaffSlice.actions.remove(meeting)),
}

const meetingInitByID = initMapFactory(fetchMeeting, listen);
registerEntityBasedFetch('staffMeeting', meetingInitByID);

const cleanup = { root: meetingStaffSlice.name, all: state => Object.values(selectMeetingData(state)), removeAll: meetingStaffSlice.actions.remove, key };
export const [useStaffMeeting] = dataHookFactory(selectMeeting, meetingInitByID, { cleanup });
export const [useStaffMeetings] = dataHookFactory(selectMeetings, initMultiMapFactory(meetingInitByID, listen), { cleanup });
export const [useStaffMeetingsInRange] = dataHookFactory(selectMeetingsInRange, queryMapFactory(fetchMeetingsInRange, 'id', meetingInitByID, listen), { cleanup });

const reducer = { name: meetingStaffSlice.name, reducer: meetingStaffSlice.reducer }
export default reducer;